import type { Conversation, TimelineEntry } from '@/types';

/**
 * 2026-10-28 (operator, after a live WhatsApp conversation in which the customer asked for a person seven times while
 * an agent read along and never took over) - "the customer asked to talk to a person".
 *
 * The bot consults the team with reason `CustomerKeyword` (Shared.Contracts `ConsultationReasons.CustomerKeyword`) when
 * the customer explicitly asks for a human. The live `ReceiveConsultation` push does NOT carry the reason; the
 * staff-only chat timeline does, as `params.reasonCode` on the `consultation_requested` entry (ConversationStoreService
 * TimelineEntryFactory.FromConsultationRequested), both on the REST page and on the live `ReceiveTimelineEntry` push.
 * So the reason is learned from the timeline and kept on the row (`Conversation.consultationReason`).
 */
export const CUSTOMER_KEYWORD_REASON = 'CustomerKeyword';

const TIMELINE_TYPE = 'consultation_requested';

/** A consultation reason with the time it was raised, from one `consultation_requested` timeline entry. */
export interface ConsultationReasonMark {
  code: string;
  at: string;
}

/** The reason carried by one timeline entry, or null when it is not a consultation request (or names no reason). */
export function reasonOfEntry(entry: TimelineEntry | null | undefined): ConsultationReasonMark | null {
  if (!entry || entry.type !== TIMELINE_TYPE) return null;
  const code = (entry.params?.reasonCode ?? '').trim();
  if (!code) return null;
  return { code, at: entry.occurredAtUtc };
}

/** The NEWEST consultation reason among `entries` (by occurrence time), or null when none names one. */
export function latestReasonOf(entries: TimelineEntry[] | null | undefined): ConsultationReasonMark | null {
  let best: ConsultationReasonMark | null = null;
  for (const e of entries ?? []) {
    const mark = reasonOfEntry(e);
    if (mark && (!best || isNewer(mark, best))) best = mark;
  }
  return best;
}

/** True when `a` happened strictly after `b` (an unparseable time is never newer). */
export function isNewer(a: ConsultationReasonMark, b: ConsultationReasonMark | null | undefined): boolean {
  if (!b) return true;
  const ta = Date.parse(a.at);
  const tb = Date.parse(b.at);
  if (!Number.isFinite(ta)) return false;
  if (!Number.isFinite(tb)) return true;
  return ta > tb;
}

/**
 * Is this conversation waiting because the CUSTOMER asked for a person?
 *
 * True only while the bot is still waiting for the team on it (CONSULTATION_REQUESTED: not taken over, not closed, not
 * dismissed - each of those moves the state, and the row's reason is cleared when it leaves) AND the newest consultation
 * reason this console knows is `CustomerKeyword`. Unknown reason = false: the banner and the badge are an extra alert,
 * never the only sign that the bot is waiting (the state badge and the Needs-consultation folder still say that).
 */
export function customerAskedForPerson(conv: Pick<Conversation, 'state' | 'consultationReason'>): boolean {
  if (conv.state !== 'CONSULTATION_REQUESTED') return false;
  const code = (conv.consultationReason?.code ?? '').trim().toLowerCase();
  return code === CUSTOMER_KEYWORD_REASON.toLowerCase();
}
